import { useState, useCallback, useMemo, JSXElementConstructor, Key, ReactElement, ReactNode, ReactPortal } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarIcon, Upload, AlertTriangle, Search, Shield, ShieldAlert, ShieldCheck, Download } from 'lucide-react';
import { format } from "date-fns";
import Papa from 'papaparse';
import _ from 'lodash';

interface Referral {
  subscriber_id: string;
  email: string;
  referrer_email: string;
  referred_at: string;
  status?: string;
}

interface EmailAnalysisResult {
  email: string;
  isFraud: boolean;
  reasons: string[];
  referrals: Referral[];
  relatedEmails: string[];
  riskScore: number;
  riskLevel: 'low' | 'medium' | 'high';
  name?: string;
  cpf?: string;
  product?: string;
}

interface ResponseData {
  email: string;  // Campo "Seu e-mail" do CSV de respostas
}

interface ParseResults {
  data: any[];
  errors: any[];
  meta: {
    delimiter: string;
    linebreak: string;
    aborted: boolean;
    truncated: boolean;
    cursor: number;
  };
}

interface ProcessedResponse {
  email: string;
  product: string;
  name: string;
  cpf: string;
}

interface CombinedAnalysisResult {
  email: string;
  name: string;
  cpf: string;
  product: string;
  isFraud: boolean;
  totalReferrals: number;
  fraudulentReferrals: number;
  riskScore: number;
  reasons: string[];
}

const SUSPICIOUS_DOMAINS = new Set([
  // Serviços de email temporário conhecidos
  'tempmail', 'throwaway', 'temp-mail', 'fakeinbox', 'guerrilla',
  'mailinator', 'yopmail', 'sharklasers', 'guerrillamail', '10minutemail',
  'trashmail', 'disposable', 'temporary-mail', 'tempinbox', 'tempmailbox',

  // Domínios suspeitos específicos
  'cironex.com', 'gianes.com', 'anypng.com', 'edectus.com', 'inikale.com',
  'lineacr.com', 'operades.com', 'opposir.com', 'spamgourmet.com', 'jetable.org',
  'mailnesia.com', 'maildrop.cc', 'harakirimail.com', 'trashmail.net',
  'mailnull.com', 'dispostable.com', 'tempmail.net', 'spamfree24.org',

  // Provedores conhecidos por spam
  'spam4.me', 'spambox.us', 'spamex.com', 'spamfree24.net', 'spamgourmet.net',
  'spamhole.com', 'spamify.com', 'spammotel.com', 'tempemail.net',

  // Domínios descartáveis populares
  'wegwerfmail.de', 'wegwerfmail.net', 'wegwerfmail.org', 'trash-mail.at',
  'trash-mail.com', 'trashmail.at', 'trashmail.me', 'einrot.com', 'fakeinbox.com',
  'tempmail.it', 'temp-mail.org', 'temp-mail.ru', 'tempmailaddress.com',

  // Outros domínios suspeitos
  'mailcatch.com', 'throwawaymail.com', 'incognitomail.com', 'getairmail.com',
  'getnada.com', 'inboxalias.com', 'killmail.net', 'tempmailer.com',
  'tempmailz.com', 'fakemailgenerator.com', 'emailondeck.com', 'emailfake.com'
]);

// Padrões suspeitos de nomes de usuário
const SUSPICIOUS_USERNAME_PATTERNS = [
  // 5+ dígitos consecutivos
  /\d{5,}/,
  // Padrão típico de nome aleatório + números (ex: gabeni1326, cirayo9411)
  /^[a-z]{4,8}\d{4,}$/,
  // Números no início e fim (ex: 10hugocarvalho10)
  /^\d+.*\d+$/,
  // Sequência aleatória de consoantes
  /[bcdfghjklmnpqrstvwxz]{5,}/,
  // Padrões repetitivos
  /(.)\1{4,}/
];

/**
 * Calcula a similaridade entre duas strings usando a distância de Levenshtein normalizada.
 * 
 * A similaridade é calculada como 1 menos a razão entre a distância de Levenshtein
 * e o comprimento da string mais longa. O resultado varia entre 0 (totalmente diferentes)
 * e 1 (strings idênticas).
 * 
 * @param str1 - Primeira string para comparação
 * @param str2 - Segunda string para comparação
 * @returns Valor entre 0 e 1 representando o grau de similaridade entre as strings
 */
function calculateSimilarity(str1: string, str2: string): number {
  const maxLength = Math.max(str1.length, str2.length);
  if (maxLength === 0) return 1.0;
  const distance = levenshteinDistance(str1, str2);
  return 1 - (distance / maxLength);
}

/**
 * Calcula a distância de Levenshtein entre duas strings.
 * 
 * A distância de Levenshtein é o número mínimo de operações de edição (inserção, 
 * deleção ou substituição) necessárias para transformar uma string em outra.
 * 
 * O algoritmo usa programação dinâmica com uma matriz dp[m+1][n+1] onde:
 * - dp[i][j] representa a distância entre os primeiros i caracteres de str1 
 *   e os primeiros j caracteres de str2
 * - A primeira linha e coluna são inicializadas com os índices (custo de deleções)
 * - Para cada posição, verifica se os caracteres são iguais (custo 0) ou diferentes
 *   (custo 1 + mínimo entre inserção, deleção ou substituição)
 * 
 * @param str1 - Primeira string para comparação
 * @param str2 - Segunda string para comparação
 * @returns Número inteiro representando a distância de Levenshtein entre as strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const m = str1.length;
  const n = str2.length;
  const dp: number[][] = Array(m + 1).fill(0).map(() => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(
          dp[i - 1][j],     // deletion
          dp[i][j - 1],     // insertion
          dp[i - 1][j - 1]  // substitution
        );
      }
    }
  }
  return dp[m][n];
}

// Padrões de email suspeitos
// Estes padrões são usados para identificar emails que podem ser fraudulentos
// ou descartáveis com base em características comuns de emails gerados automaticamente.
const SUSPICIOUS_PATTERNS = [
  // Emails que começam com letras seguidas de 4 ou mais dígitos
  /^[a-z]+\d{4,}@/i,
  // Emails que começam com dígitos, seguidos de letras e mais dígitos
  /^\d+[a-z]+\d+@/i,
  // Emails que têm entre 2 a 6 letras seguidas de 2 a 6 dígitos
  /^[a-z]{2,6}\d{2,6}@/i
].map(pattern => new RegExp(pattern));

/**
 * Define os níveis de recompensa do programa de referência
 * 
 * Cada nível (tier) contém:
 * @property {string} name - Identificador único do nível de recompensa
 * @property {number} min - Número mínimo de referências necessárias para este nível
 * @property {number} max - Número máximo de referências para este nível (Infinity para o último nível)
 * @property {string} label - Nome amigável da recompensa para exibição
 * 
 * Os níveis são:
 * - 5-14 referências: Report do Grupo Waffle
 * - 15-29 referências: Adesivos
 * - 30-59 referências: Porta Cartão
 * - 60-99 referências: Moleskine
 * - 100-199 referências: Caneca
 * - 200+ referências: Mochila
 */
const REWARD_TIERS = [
  { name: 'report', min: 5, max: 14, label: 'Report do Grupo Waffle' },
  { name: 'adesivos', min: 15, max: 29, label: 'Adesivos' },
  { name: 'porta_cartao', min: 30, max: 59, label: 'Porta Cartão' },
  { name: 'moleskine', min: 60, max: 99, label: 'Moleskine' },
  { name: 'caneca', min: 100, max: 199, label: 'Caneca' },
  { name: 'mochila', min: 200, max: Infinity, label: 'Mochila' }
];

/**
 * Componente principal para detecção de fraudes em referrals
 * 
 * Este componente permite:
 * - Upload de arquivo CSV com dados de referrals
 * - Filtragem por data inicial
 * - Análise individual de emails
 * - Visualização de estatísticas gerais
 * - Listagem dos top 10 referrers legítimos
 */
const ReferralFraudDetector = () => {
  // Estado para controle de loading
  const [loading, setLoading] = useState(false);
  // Estado para mensagens de erro
  const [error, setError] = useState<string | null>(null);
  // Lista dos top referrers após análise
  const [topReferrers, setTopReferrers] = useState<any[]>([]);
  // Data inicial para filtrar referrals
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  // Estatísticas gerais de fraude
  const [fraudStats, setFraudStats] = useState<{
    total: number;    // Total de referrals analisados
    fraud: number;    // Quantidade detectada como fraude
    legitimate: number; // Quantidade de referrals legítimos
  }>({ total: 0, fraud: 0, legitimate: 0 });
  // Progresso do processamento do arquivo
  const [, setProgress] = useState(0);
  // Dados do CSV carregado
  const [csvData, setCsvData] = useState<Referral[]>([]);
  // Email sendo pesquisado para análise
  const [searchEmail, setSearchEmail] = useState('');
  // Resultado da análise do email pesquisado
  const [emailAnalysis, setEmailAnalysis] = useState<EmailAnalysisResult | null>(null);
  // Adicione um novo estado para armazenar os resultados processados
  const [processedResults, setProcessedResults] = useState<{
    legitimate: Referral[];
    fraudSet: Set<string>;
  } | null>(null);
  // Dados do arquivo de respostas carregado
  const [_responseData, setResponseData] = useState<ResponseData[]>([]);
  // Controla se o arquivo de respostas foi carregado
  const [responseFileUploaded, setResponseFileUploaded] = useState(false);
  // Resultado da análise combinada de referrals e respostas
  const [combinedAnalysis, setCombinedAnalysis] = useState<CombinedAnalysisResult[]>([]);
  // Lista de emails removidos da análise
  const [removedEmails, setRemovedEmails] = useState<Array<{
    fraudulentReferrals: any;
    riskScore: ReactNode;
    reasons: any;
    email: string;
    isFraud: boolean;
    totalReferrals: number;
  }>>([]);
  // Cache para resultados de análise de email
  const [emailAnalysisCache] = useState(new Map<string, EmailAnalysisResult>());
  // Controla se uma análise está em andamento
  const [analysisInProgress, setAnalysisInProgress] = useState(false);
  // Progresso da análise atual (0-100)
  const [analysisProgress, setAnalysisProgress] = useState(0);

  /**
   * Verifica se um domínio de email é considerado suspeito
   * @param domain Domínio do email a ser verificado
   * @returns true se o domínio for suspeito, false caso contrário
   */
  const isDomainSuspicious = useCallback((domain: string): boolean => {
    return Array.from(SUSPICIOUS_DOMAINS).some(suspiciousDomain =>
      domain.toLowerCase().includes(suspiciousDomain.toLowerCase())
    );
  }, []);

  /**
   * Hook memorizado para verificar se um email é descartável
   * Utiliza cache para otimizar verificações repetidas
   */
  const isDisposableEmail = useMemo(() => {
    const emailCache = new Map<string, boolean>();

    return (email: string): boolean => {
      if (emailCache.has(email)) {
        return emailCache.get(email)!;
      }

      const lowerEmail = email.toLowerCase();
      const [localPart, domain] = lowerEmail.split('@');

      // Verifica domínios suspeitos
      if (Array.from(SUSPICIOUS_DOMAINS).some(d => domain.includes(d))) {
        emailCache.set(email, true);
        return true;
      }

      // Verifica padrões suspeitos no nome de usuário
      if (SUSPICIOUS_USERNAME_PATTERNS.some(pattern => pattern.test(localPart))) {
        emailCache.set(email, true);
        return true;
      }

      if (SUSPICIOUS_PATTERNS.some(pattern => pattern.test(localPart))) {
        emailCache.set(email, true);
        return true;
      }

      // Verifica características numéricas suspeitas
      const numberCount = (localPart.match(/\d/g) || []).length;
      if (numberCount >= 4) { // Muitos números é suspeito
        emailCache.set(email, true);
        return true;
      }

      // Verifica proporção de números versus letras
      const letterCount = (localPart.match(/[a-z]/g) || []).length;
      if (numberCount > 0 && numberCount / letterCount > 0.5) { // Mais de 50% números
        emailCache.set(email, true);
        return true;
      }

      emailCache.set(email, false);
      return false;
    };
  }, []);

  /**
   * Hook memorizado para verificar similaridade entre dois emails
   * Utiliza cache para otimizar verificações repetidas
   */
  const isSimilarEmail = useMemo(() => {
    const similarityCache = new Map<string, boolean>();

    return (email1: string, email2: string): boolean => {
      const key = `${email1}:${email2}`;
      if (similarityCache.has(key)) {
        return similarityCache.get(key)!;
      }

      const [user1, domain1] = email1.toLowerCase().split('@');
      const [user2, domain2] = email2.toLowerCase().split('@');

      if (domain1 !== domain2) {
        similarityCache.set(key, false);
        return false;
      }

      const cleanUser1 = user1.replace(/[0-9._-]/g, '');
      const cleanUser2 = user2.replace(/[0-9._-]/g, '');

      const result = cleanUser1.length > 0 &&
        cleanUser2.length > 0 &&
        (cleanUser1.includes(cleanUser2) || cleanUser2.includes(cleanUser1));

      similarityCache.set(key, result);
      return result;
    };
  }, []);

  /**
   * Analisa um email referido para detectar padrões suspeitos
   * @param email Email a ser analisado
   * @returns Objeto contendo razões de suspeita, score de risco e flag de suspeita
   */
  const analyzeReferredEmail = useCallback((email: string): {
    reasons: string[],
    riskScore: number,
    isSuspicious: boolean
  } => {
    const reasons: string[] = [];
    let riskScore = 0;

    const [localPart, domain] = email.toLowerCase().split('@');

    if (isDomainSuspicious(domain)) {
      reasons.push(`Email ${email} usa domínio suspeito (${domain})`);
      riskScore += 35;
    }

    const repeatingPatterns = (localPart.match(/(.)\1{2,}/g) || []).length;
    if (repeatingPatterns > 0) {
      reasons.push(`Email ${email} contém padrões repetitivos de caracteres`);
      riskScore += repeatingPatterns * 10;
    }

    const consecutiveDigits = (localPart.match(/\d{3,}/g) || []).length;
    if (consecutiveDigits > 0) {
      reasons.push(`Email ${email} contém sequência suspeita de números`);
      riskScore += consecutiveDigits * 20;
    }

    const digitCount = (localPart.match(/\d/g) || []).length;
    const digitPercentage = digitCount / localPart.length;
    if (digitPercentage > 0.4) {
      reasons.push(`Email ${email} possui alta proporção de números`);
      riskScore += Math.floor(digitPercentage * 50);
    }

    if (/^\d+.*\d+$/.test(localPart)) {
      reasons.push(`Email ${email} possui números no início e fim`);
      riskScore += 25;
    }

    return {
      reasons,
      riskScore,
      isSuspicious: riskScore >= 50
    };
  }, [isDomainSuspicious]);

  /**
   * Realiza análise completa de um email
   * Verifica padrões suspeitos, histórico de referrals e emails relacionados
   */
  const analyzeEmail = useCallback((email: string): EmailAnalysisResult => {
    if (!csvData.length) {
      setError('Por favor, carregue um arquivo CSV primeiro');
      return {
        email,
        isFraud: false,
        reasons: [],
        referrals: [],
        relatedEmails: [],
        riskScore: 0,
        riskLevel: 'low'
      };
    }

    const reasons: string[] = [];
    const relatedEmails: Set<string> = new Set();
    let riskScore = 0;

    const [localPart, domain] = email.toLowerCase().split('@');

    if (isDomainSuspicious(domain)) {
      reasons.push('Domínio conhecido por emails descartáveis');
      riskScore += 35;
    }

    const allLocalParts = csvData.map(ref => ref.email.split('@')[0].toLowerCase());

    const similarEmails = allLocalParts.filter(otherLocal => {
      if (otherLocal === localPart) return false;
      return calculateSimilarity(localPart, otherLocal) >= 0.7;
    });

    if (similarEmails.length > 0) {
      reasons.push(`${similarEmails.length} email(s) com alta similaridade detectados`);
      riskScore += similarEmails.length * 15;
    }

    const asReferrer = csvData.filter(ref => ref.referrer_email.toLowerCase() === email.toLowerCase());
    const asReferred = csvData.filter(ref => ref.email.toLowerCase() === email.toLowerCase());

    if (isDisposableEmail(email)) {
      reasons.push('Email descartável detectado');
      riskScore += 50;
    }

    // Analyze all referred emails
    if (asReferrer.length > 0) {
      const suspiciousReferrals = asReferrer
        .map(ref => ({
          referral: ref,
          analysis: analyzeReferredEmail(ref.email)
        }))
        .filter(result => result.analysis.isSuspicious);

      if (suspiciousReferrals.length > 0) {
        reasons.push(...suspiciousReferrals.flatMap(result => result.analysis.reasons));
        riskScore += Math.min(suspiciousReferrals.length * 30, 100);

        const suspiciousDomains = new Set(
          suspiciousReferrals
            .map(result => result.referral.email.split('@')[1])
        );

        if (suspiciousDomains.size > 0) {
          reasons.push(`Indicou ${suspiciousReferrals.length} emails suspeitos em ${suspiciousDomains.size} domínios diferentes`);
        }
      }

      // Check for rapid referrals
      const referralDates = asReferrer.map(ref => new Date(ref.referred_at).getTime());
      const sortedDates = referralDates.sort((a, b) => a - b);
      const timeIntervals = sortedDates.slice(1).map((date, i) => date - sortedDates[i]);
      const rapidReferrals = timeIntervals.filter(interval => interval < 30000).length;

      if (rapidReferrals > 0) {
        reasons.push(`${rapidReferrals} referrals feitos em menos de 30 segundos`);
        riskScore += 30;
      }

      // Check for similar email patterns in referred users
      asReferrer.forEach(ref => {
        if (isSimilarEmail(email, ref.email)) {
          relatedEmails.add(ref.email);
          riskScore += 20;
        }
      });
    }

    let riskLevel: 'low' | 'medium' | 'high' = 'low';
    if (riskScore >= 70) riskLevel = 'high';
    else if (riskScore >= 30) riskLevel = 'medium';

    if (relatedEmails.size > 0) {
      reasons.push(`${relatedEmails.size} emails similares encontrados`);
    }

    const analysis: EmailAnalysisResult = {
      email,
      isFraud: riskScore >= 70,
      reasons,
      referrals: [...asReferrer, ...asReferred],
      relatedEmails: Array.from(relatedEmails),
      riskScore,
      riskLevel
    };

    setEmailAnalysis(analysis);
    return analysis;
  }, [csvData, isDisposableEmail, isSimilarEmail, analyzeReferredEmail, isDomainSuspicious]);

  /**
   * Processa um lote de referrals para detecção de fraudes
   * @param referrals Lista de referrals para processar
   * @param batchSize Tamanho do lote para processamento (default: 100)
   * @returns Promise com referrals legítimos e conjunto de IDs fraudulentos
   */
  const processBatch = useCallback(async (
    referrals: Referral[],
    batchSize: number = 100
  ): Promise<{ legitimate: Referral[], fraudSet: Set<string> }> => {
    const fraudSet = new Set<string>();
    const referrerGroups = _.groupBy(referrals, 'referrer_email');
    const totalGroups = Object.keys(referrerGroups).length;
    let processedGroups = 0;

    const processBatchPromise = new Promise<{ legitimate: Referral[], fraudSet: Set<string> }>((resolve) => {
      const processNextBatch = () => {
        const batchEntries = Object.entries(referrerGroups)
          .slice(processedGroups, processedGroups + batchSize);

        batchEntries.forEach(([referrerEmail, groupReferrals]) => {
          // Verifica se o email do indicador é descartável
          if (isDisposableEmail(referrerEmail)) {
            groupReferrals.forEach(ref => fraudSet.add(ref.subscriber_id));
            return;
          }

          // Agrupa por padrão de email (removendo números e caracteres especiais)
          const emailGroups = _.groupBy(groupReferrals, ref =>
            ref.email.split('@')[0].replace(/[0-9._-]/g, '')
          );

          // Verifica padrões suspeitos em cada grupo
          Object.values(emailGroups).forEach(group => {
            if (group.length > 1) {
              const emails = group.map(ref => ref.email);
              for (let i = 0; i < emails.length - 1; i++) {
                if (isSimilarEmail(emails[i], emails[i + 1])) {
                  group.forEach(ref => fraudSet.add(ref.subscriber_id));
                  break;
                }
              }
            }
          });
        });

        processedGroups += batchSize;
        const progress = Math.min((processedGroups / totalGroups) * 100, 100);
        setProgress(Math.floor(progress));

        if (processedGroups < totalGroups) {
          setTimeout(processNextBatch, 0);
        } else {
          // Filtra indicadores com alta taxa de fraude
          const legitimateReferrals = filterHighFraudRateReferrers(referrals, fraudSet);
          resolve({ legitimate: legitimateReferrals, fraudSet });
        }
      };

      processNextBatch();
    });

    return processBatchPromise;
  }, [isDisposableEmail, isSimilarEmail]);

  /**
 * Filtra referrals removendo indicadores com alta taxa de fraude
 */
  const filterHighFraudRateReferrers = (referrals: Referral[], fraudSet: Set<string>): Referral[] => {
    const referrerStats = new Map<string, { total: number, frauds: number }>();

    // Calcula estatísticas por indicador
    referrals.forEach(referral => {
      const stats = referrerStats.get(referral.referrer_email) || { total: 0, frauds: 0 };
      stats.total++;
      if (fraudSet.has(referral.subscriber_id)) {
        stats.frauds++;
      }
      referrerStats.set(referral.referrer_email, stats);
    });

    // Filtra indicadores com taxa de fraude menor que 70%
    const validReferrers = new Set(
      Array.from(referrerStats.entries())
        .filter(([, stats]) => (stats.frauds / stats.total) < 0.7)
        .map(([email]) => email)
    );

    // Retorna apenas referrals de indicadores válidos e que não são fraudes
    return referrals.filter(ref =>
      validReferrers.has(ref.referrer_email) && !fraudSet.has(ref.subscriber_id)
    );
  };

  /**
   * Processa arquivo CSV carregado
   * @param file Arquivo CSV contendo dados de referrals
   */
  const processFile = useCallback(async (file: File) => {
    setLoading(true);
    setError(null);
    setProgress(0);
    setEmailAnalysis(null);

    try {
      const text = await file.text();
      
      if (!text.trim()) {
        setError('O arquivo está vazio');
        setLoading(false);
        return;
      }

      const parseConfig = {
        header: true,
        skipEmptyLines: true,
        preview: 10000,
        complete: async (results: ParseResults) => {
          try {
            if (!results.data || results.data.length === 0) {
              setError('Nenhum dado válido encontrado no arquivo');
              setLoading(false);
              return;
            }

            const data = results.data as Referral[];
            
            let validData = data.filter(row => {
              return row && 
                     typeof row === 'object' && 
                     row.email && 
                     row.referrer_email && 
                     row.referred_at;
            });

            if (validData.length === 0) {
              setError('Nenhum dado válido após filtragem');
              setLoading(false);
              return;
            }

            if (startDate) {
              validData = validData.filter(row => {
                try {
                  const referralDate = new Date(row.referred_at.replace('+', ''));
                  return !isNaN(referralDate.getTime()) && referralDate >= startDate;
                } catch {
                  return false;
                }
              });
            }

            setCsvData(validData);

            const batchSize = 100;
            const processedData = await processBatch(validData, batchSize);
            
            if (!processedData) {
              throw new Error('Erro ao processar dados');
            }

            setProcessedResults(processedData);

            setFraudStats({
              total: validData.length,
              fraud: processedData.fraudSet.size,
              legitimate: processedData.legitimate.length
            });

            const referrerCounts = _.countBy(processedData.legitimate, 'referrer_email');
            const sortedReferrers = Object.entries(referrerCounts)
              .map(([email, count]) => ({ email, count }))
              .sort((a, b) => b.count - a.count)
              .slice(0, 10);
            
            setTopReferrers(sortedReferrers);

          } catch (err) {
            console.error('Erro no processamento:', err);
            setError('Erro ao processar dados: ' + (err instanceof Error ? err.message : 'Erro desconhecido'));
          } finally {
            setLoading(false);
            setProgress(0);
          }
        },
        error: (error: Error) => {
          console.error('Erro no parse:', error);
          setError('Erro ao analisar CSV: ' + error.message);
          setLoading(false);
          setProgress(0);
        }
      };

      const parseTimeout = setTimeout(() => {
        if (loading) {
          setError('Tempo limite excedido ao processar arquivo');
          setLoading(false);
        }
      }, 30000);

      Papa.parse(text, parseConfig);

      return () => clearTimeout(parseTimeout);

    } catch (err) {
      console.error('Erro na leitura:', err);
      setError('Erro ao ler arquivo: ' + (err instanceof Error ? err.message : 'Erro desconhecido'));
      setLoading(false);
      setProgress(0);
    }
  }, [startDate, processBatch, loading]);

  /**
   * Manipula a pesquisa de email para análise
   */
  const handleEmailSearch = () => {
    if (!searchEmail.trim()) {
      setError('Por favor, insira um email para análise');
      return;
    }
    analyzeEmail(searchEmail.trim());
  };

  /**
   * Retorna a cor do badge baseado no nível de risco
   * @param riskLevel Nível de risco (low, medium, high)
   * @returns Classes CSS para estilização do badge
   */
  const getRiskBadgeColor = (riskLevel: 'low' | 'medium' | 'high') => {
    switch (riskLevel) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-red-100 text-red-800';
    }
  };

  /**
   * Função auxiliar para download de CSV
   */
  const downloadCSV = (data: any[], filename: string) => {
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * Gera relatório CSV de referrals válidos
   */
  const downloadValidReferrals = () => {
    if (!processedResults || !csvData.length) {
      setError('Nenhum dado processado disponível');
      return;
    }

    const { legitimate } = processedResults;

    // Agrupa por email do indicador
    const groupedReferrals = _.groupBy(legitimate, 'referrer_email');

    const reportData = Object.entries(groupedReferrals)
      .map(([referrerEmail, referrals]) => {
        const referrerCount = referrals.length;
        const rewardTier = REWARD_TIERS.find(
          tier => referrerCount >= tier.min && referrerCount <= tier.max
        );

        // Pula se não tiver recompensa
        if (!rewardTier) return null;

        // Ordena referrals por data
        const sortedReferrals = _.sortBy(referrals, 'referred_at');
        const firstDate = sortedReferrals[0].referred_at;
        const lastDate = sortedReferrals[sortedReferrals.length - 1].referred_at;

        // Lista de emails indicados
        const referredEmails = sortedReferrals.map(r => r.email).join(', ');

        return {
          'Email do Indicador': referrerEmail,
          'Recompensa': rewardTier.label,
          'Total de Referrals': referrerCount,
          'Primeiro Referral': format(new Date(firstDate), 'dd/MM/yyyy'),
          'Ultimo Referral': format(new Date(lastDate), 'dd/MM/yyyy'),
          'Emails Indicados': referredEmails
        };
      })
      .filter(Boolean); // Remove os nulls

    // Ordena por total de indicações (maior primeiro)
    const sortedReportData = _.orderBy(reportData, ['Total de Referrals'], ['desc']);

    downloadCSV(sortedReportData, 'indicadores_premiados.csv');
  };

  const analyzeEmailsBatch = useCallback(async (emails: string[], batchSize = 25) => {
    const results: EmailAnalysisResult[] = [];
    const totalEmails = emails.length;
    let processedCount = 0;

    // Função para processar um único lote
    const processBatch = async (batch: string[]) => {
      return Promise.all(batch.map(async email => {
        if (emailAnalysisCache.has(email)) {
          return emailAnalysisCache.get(email)!;
        }
        
        const analysis = analyzeEmail(email);
        emailAnalysisCache.set(email, analysis);
        return analysis;
      }));
    };

    // Processa em lotes menores com delay entre eles
    for (let i = 0; i < totalEmails; i += batchSize) {
      const batch = emails.slice(i, i + batchSize);
      
      const batchResults = await processBatch(batch);
      results.push(...batchResults);
      
      processedCount += batch.length;
      setAnalysisProgress((processedCount / totalEmails) * 100);
      
      // Aumenta o delay entre lotes para evitar travamento
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return results;
  }, [analyzeEmail, emailAnalysisCache]);

  const analyzeCombinedData = useCallback(async (responses: ProcessedResponse[]) => {
    if (!processedResults) return;
    
    setAnalysisInProgress(true);
    setAnalysisProgress(0);

    try {
      // Remove duplicatas antes de processar
      const uniqueEmails = Array.from(new Set(
        responses.map(r => r.email.toLowerCase())
      ));

      // Analisa emails em lotes menores
      const emailAnalyses = await analyzeEmailsBatch(uniqueEmails);

      // Processa os resultados em lotes para não travar a UI
      const analysis = [];
      const batchSize = 50;

      for (let i = 0; i < emailAnalyses.length; i += batchSize) {
        const batch = emailAnalyses.slice(i, i + batchSize);
        
        const batchAnalysis = batch.map(emailAnalysis => {
          const referrals = csvData.filter(ref => 
            ref.referrer_email.toLowerCase() === emailAnalysis.email.toLowerCase()
          );

          const fraudulentReferrals = referrals.filter(ref => 
            processedResults.fraudSet.has(ref.subscriber_id)
          );

          // Adiciona informações do produto
          const response = responses.find(r => 
            r.email.toLowerCase() === emailAnalysis.email.toLowerCase()
          );

          return {
            email: emailAnalysis.email,
            product: response?.product || 'N/A',
            name: response?.name || 'N/A',
            cpf: response?.cpf || 'N/A',
            isFraud: emailAnalysis.isFraud || fraudulentReferrals.length > 0,
            totalReferrals: referrals.length,
            fraudulentReferrals: fraudulentReferrals.length,
            riskScore: emailAnalysis.riskScore,
            reasons: emailAnalysis.reasons
          };
        });

        analysis.push(...batchAnalysis);
        
        // Atualiza a UI gradualmente
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      setCombinedAnalysis(analysis);
    } finally {
      setAnalysisInProgress(false);
      setAnalysisProgress(0);
    }
  }, [csvData, processedResults, analyzeEmailsBatch]);

  const processResponseFile = useCallback(async (file: File) => {
    setLoading(true);
    setError(null);

    try {
      const text = await file.text();
      
      if (!text.trim()) {
        setError('O arquivo de respostas está vazio');
        setLoading(false);
        return;
      }

      // Divide o texto em linhas
      const lines = text.split('\n');
      const responses: ProcessedResponse[] = [];
      let currentProduct = '';

      // Processa linha por linha
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();

        // Pula linhas vazias
        if (!line) continue;

        // Verifica se é uma linha de cabeçalho de produto
        if (line && !line.includes(',')) {
          currentProduct = line;
          i++; // Pula a próxima linha (cabeçalho das colunas)
          continue;
        }

        // Processa as linhas de dados
        if (currentProduct && line.includes(',')) {
          const columns = line.split(',').map(col => col.trim().replace(/^"|"$/g, ''));
          
          // Verifica se temos as colunas necessárias
          if (columns.length >= 5) {
            const response: ProcessedResponse = {
              email: columns[4] || '', // Coluna "Email"
              product: currentProduct,
              name: columns[3] || '', // Coluna "Nome Completo"
              cpf: columns[5] || '', // Coluna "CPF"
            };

            // Só adiciona se tiver email
            if (response.email && response.email !== 'Email') {
              responses.push(response);
            }
          }
        }
      }

      if (responses.length === 0) {
        throw new Error('Nenhuma resposta válida encontrada no arquivo');
      }

      // Remove duplicatas baseado no email
      const uniqueResponses = responses.filter((response, index, self) =>
        index === self.findIndex((r) => r.email === response.email)
      );

      setResponseData(uniqueResponses);
      setResponseFileUploaded(true);
      analyzeCombinedData(uniqueResponses);

    } catch (err) {
      console.error('Erro na leitura de respostas:', err);
      setError('Erro ao ler arquivo de respostas: ' + (err instanceof Error ? err.message : 'Erro desconhecido'));
      setLoading(false);
    }
  }, [analyzeCombinedData]);

  const downloadValidResponseReferrals = useCallback(() => {
    if (!combinedAnalysis.length) return;

    const validEmails = combinedAnalysis.filter(analysis => 
      !analysis.isFraud && 
      analysis.totalReferrals > 0 && 
      analysis.riskScore < 70 && 
      analysis.fraudulentReferrals === 0
    );

    const fraudulentEmails = combinedAnalysis.filter(analysis => 
      analysis.isFraud || 
      analysis.totalReferrals === 0 || 
      analysis.riskScore >= 70 || 
      analysis.fraudulentReferrals > 0
    ).map(email => ({
      ...email,
      reasons: email.reasons || []
    }));

    // Prepara dados para exportação
    const validData = validEmails.map(analysis => ({
      'Email': analysis.email,
      'Nome': analysis.name,
      'CPF': analysis.cpf,
      'Produto': analysis.product,
      'Total de Referrals Válidos': analysis.totalReferrals
    }));

    downloadCSV(validData, 'emails_validos.csv');

    // Atualiza o estado para mostrar os emails removidos
    setRemovedEmails(fraudulentEmails);
  }, [combinedAnalysis]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Detector de Fraudes em Referrals</CardTitle>
          <CardDescription>
            Faça upload do CSV e analise referrals suspeitos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-64">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "dd/MM/yyyy") : "Selecionar data inicial"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {startDate && (
                <Button
                  variant="outline"
                  onClick={() => setStartDate(undefined)}
                  className="text-red-500 hover:text-red-600"
                >
                  Limpar data
                </Button>
              )}
            </div>

            <div className="flex justify-center items-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 mb-4 text-gray-500" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Clique para fazer upload</span> ou arraste e solte
                  </p>
                  <p className="text-xs text-gray-500">CSV (max. 200MB)</p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) processFile(file);
                  }}
                />
              </label>
            </div>

            {csvData.length > 0 && !responseFileUploaded && (
              <div className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Upload CSV de Respostas</CardTitle>
                    <CardDescription>
                      Faça upload do CSV com as respostas do formulário para análise cruzada
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-center items-center w-full">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <Upload className="w-6 h-6 mb-2 text-gray-500" />
                          <p className="text-sm text-gray-500">
                            Clique para fazer upload do CSV de respostas
                          </p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept=".csv"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) processResponseFile(file);
                          }}
                        />
                      </label>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {combinedAnalysis.length > 0 && (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle>Análise de Fraudes</CardTitle>
                  <CardDescription>
                    {combinedAnalysis.filter(a => !a.isFraud).length} emails válidos encontrados
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <Button
                      onClick={downloadValidResponseReferrals}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Baixar Emails Válidos
                    </Button>
                    
                    <div className="mt-4">
                      <p className="text-sm text-gray-600">
                        {combinedAnalysis.filter(a => a.isFraud).length} emails foram removidos por terem indicado fraudes
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {removedEmails.length > 0 && (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle>Emails Removidos</CardTitle>
                  <CardDescription>
                    {removedEmails.length} emails foram removidos da exportação
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px] pr-4" scrollHideDelay={0} type="always">
                    <div className="space-y-2">
                      {removedEmails.map((email, index) => (
                        <div key={index} className="p-3 border rounded-lg">
                          <div className="flex flex-col gap-2">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-medium">{email.email}</p>
                                <p className="text-sm text-gray-500">
                                  {email.totalReferrals === 0 
                                    ? "Removido: Sem referrals válidos"
                                    : `Removido: ${email.fraudulentReferrals} de ${email.totalReferrals} referrals são fraudulentos`}
                                </p>
                              </div>
                              <div className="text-sm">
                                <p className="text-gray-500">Score de Risco: {email.riskScore}</p>
                                {email.fraudulentReferrals > 0 && (
                                  <p className="text-red-500">
                                    {email.fraudulentReferrals} fraudulentos
                                  </p>
                                )}
                              </div>
                            </div>
                            {email.reasons.length > 0 && (
                              <div className="mt-2 text-sm text-gray-600">
                                <p className="font-medium">Motivos:</p>
                                <ul className="list-disc list-inside">
                                  {email.reasons.map((reason: string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | null | undefined, idx: Key | null | undefined) => (
                                    <li key={idx}>{reason}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}

            {/* Email Analysis Section */}
            <div className="mt-8 space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <Input
                    type="email"
                    placeholder="Digite o email para análise"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    className="w-full"
                  />
                </div>
                <Button
                  onClick={handleEmailSearch}
                  disabled={!csvData.length || !searchEmail.trim()}
                  className="bg-[#FF3366] text-white hover:bg-[#FF3366]/90"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Analisar Email
                </Button>
              </div>

              {emailAnalysis && (
                <Card className="mt-4">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Análise de Risco: {emailAnalysis.email}</CardTitle>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskBadgeColor(emailAnalysis.riskLevel)}`}>
                        {emailAnalysis.riskLevel === 'low' && <ShieldCheck className="w-4 h-4 inline mr-1" />}
                        {emailAnalysis.riskLevel === 'medium' && <Shield className="w-4 h-4 inline mr-1" />}
                        {emailAnalysis.riskLevel === 'high' && <ShieldAlert className="w-4 h-4 inline mr-1" />}
                        Risco {emailAnalysis.riskLevel === 'low' ? 'Baixo' : emailAnalysis.riskLevel === 'medium' ? 'Médio' : 'Alto'}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium mb-2">Score de Risco</h4>
                        <div className="flex items-center">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${emailAnalysis.riskScore >= 70 ? 'bg-red-500' :
                                  emailAnalysis.riskScore >= 30 ? 'bg-yellow-500' :
                                    'bg-green-500'
                                }`}
                              style={{ width: `${Math.min(100, emailAnalysis.riskScore)}%` }}
                            />
                          </div>
                          <span className="ml-4 font-medium">{emailAnalysis.riskScore}/100</span>
                        </div>
                      </div>

                      {emailAnalysis.reasons.length > 0 && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="font-medium mb-2">Razões para Suspeita</h4>
                          <ul className="space-y-2">
                            {emailAnalysis.reasons.map((reason, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5" />
                                <span>{reason}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {emailAnalysis.referrals.length > 0 && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="font-medium mb-2">Histórico de Referrals</h4>
                          <ScrollArea className="h-48">
                            <div className="space-y-2">
                              {emailAnalysis.referrals.map((referral, index) => (
                                <div key={index} className="bg-white p-3 rounded-md shadow-sm">
                                  <div className="flex justify-between">
                                    <span className="text-sm font-medium">
                                      {referral.email === emailAnalysis.email ? 'Referred by' : 'Referred'}:
                                      {' '}
                                      {referral.email === emailAnalysis.email ? referral.referrer_email : referral.email}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                      {format(new Date(referral.referred_at), 'dd/MM/yyyy HH:mm')}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </div>
                      )}

                      {emailAnalysis.relatedEmails.length > 0 && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="font-medium mb-2">Emails Relacionados</h4>
                          <div className="space-y-1">
                            {emailAnalysis.relatedEmails.map((email, index) => (
                              <div key={index} className="text-sm">
                                {email}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {loading && (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
              </div>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {fraudStats.total > 0 && (
              <div className="grid gap-4 md:grid-cols-3">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600">Referrals Válidos</p>
                  <p className="text-2xl font-bold text-green-700">
                    {fraudStats.legitimate}
                  </p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-600">Fraudes Detectadas</p>
                  <p className="text-2xl font-bold text-red-700">
                    {fraudStats.fraud}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600">Taxa de Fraude</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {((fraudStats.fraud / fraudStats.total) * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
            )}

            {topReferrers.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Top 10 Referrers Legítimos</CardTitle>
                  <CardDescription>
                    {startDate
                      ? `Referrals a partir de ${format(startDate, "dd/MM/yyyy")}`
                      : "Todos os referrals"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px] pr-4" scrollHideDelay={0} type="always">
                    <div className="space-y-4">
                      {topReferrers.map((referrer, index) => {
                        const rewardTier = REWARD_TIERS.find(
                          tier => referrer.count >= tier.min && referrer.count <= tier.max
                        );

                        return (
                          <div
                            key={referrer.email}
                            className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                          >
                            <div className="flex items-center gap-4">
                              <span className="text-2xl font-bold text-gray-400">
                                #{index + 1}
                              </span>
                              <div>
                                <p className="font-medium">{referrer.email}</p>
                                <p className="text-sm text-gray-500">
                                  {referrer.count} referrals válidos
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end">
                              <span className="font-medium text-blue-600">
                                {rewardTier?.label}
                              </span>
                              <span className="text-sm text-gray-500">
                                Tier: {rewardTier?.name}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}

            {/* Adicione os botões de download após as estatísticas */}
            {fraudStats.total > 0 && (
              <div className="flex gap-4 mt-4">
                <Button
                  onClick={downloadValidReferrals}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Referrals Válidos
                </Button>
              </div>
            )}

            {combinedAnalysis.length > 0 && (
              <div className="flex gap-4 mt-4">
                <Button
                  onClick={downloadValidResponseReferrals}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Emails Válidos
                </Button>
              </div>
            )}

            {analysisInProgress && (
              <div className="mt-4">
                <Card>
                  <CardContent className="py-6">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Analisando emails...</span>
                        <span>{Math.round(analysisProgress)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${analysisProgress}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralFraudDetector;